import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import axios from 'axios';
import {App} from './App/App';
import * as serviceWorker from './serviceWorker';
import { store } from './_helpers';
import config from './config';

// Detect the error and force a reload
window.addEventListener('unhandledrejection', (event) => {
  if (event.reason && event.reason.name === 'ChunkLoadError') {
    window.location.reload();
  }
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    const shouldTryRefresh = (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url.includes('/auth/refresh') &&
      !originalRequest.url.includes('/login')
    );

    if (!shouldTryRefresh) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(token => {
        originalRequest.headers.Authorization = 'Bearer ' + token;
        return axios(originalRequest);
      }).catch(err => Promise.reject(err));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const currentUser = store.getState().authentication.user;

      if (!currentUser || !currentUser.refresh_token) {
        throw new Error('No refresh token available');
      }

      const refreshResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/refresh`,
        { refresh_token: currentUser.refresh_token }
      );

      const newAccessToken = refreshResponse.data.access_token;
      const newRefreshToken = refreshResponse.data.refresh_token;

      store.dispatch({
        type: 'TOKEN_REFRESHED',
        payload: {
          access_token: newAccessToken,
          refresh_token: newRefreshToken,
        }
      });

      // Update the default header for future requests
      axios.defaults.headers.common['Authorization'] = 'Bearer ' + newAccessToken;

      processQueue(null, newAccessToken);

      originalRequest.headers.Authorization = 'Bearer ' + newAccessToken;
      return axios(originalRequest);

    } catch (refreshError) {
      processQueue(refreshError, null);
      store.dispatch({ type: 'TOKEN_UNAUTHORISED' });
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

// Sync tokens across tabs
window.addEventListener('storage', (e) => {
  const userKey = `${process.env.REACT_APP_LOCAL_STORAGE_PREFIX}user`;

  if (e.key !== userKey) return;

  const currentUser = store.getState().authentication.user;

  if (e.newValue) {
    try {
      const updatedUser = JSON.parse(e.newValue);

      // Validation: ignore incomplete updates
      if (!updatedUser?.token || !updatedUser?.refresh_token) {
        console.warn('Storage event with incomplete user, ignoring');
        return;
      }

      // Only proceed if the current user exists AND the tokens are different
      if (currentUser && currentUser.token !== updatedUser.token) {
        store.dispatch({
          type: 'TOKEN_REFRESHED',
          payload: {
            access_token: updatedUser.token,
            refresh_token: updatedUser.refresh_token,
          }
        });
      }
    } catch (err) {
      console.warn('Storage event with invalid JSON, ignoring:', err);
    }
  } else {
    if (currentUser) {
      store.dispatch({ type: 'TOKEN_UNAUTHORISED' });
    }
  }
});

const app = (
  <Provider store={store}>
    <BrowserRouter basename={config.basename}>
      {/* basename="/datta-able" */}
      <App />
    </BrowserRouter>
  </Provider>
);

ReactDOM.render(app, document.getElementById('root'));

serviceWorker.unregister();